import React, { useState, useEffect, useCallback } from "react";
import API from "../../API/newIndex";
import { ArrowLeft } from "feather-icons-react/build/IconComponents";
import Breadcrumbs from "../../controllers/breadCrumbs";
import { parentNavigate } from "../../controllers/ParentNavigate";
import Notification from "../Notification";
import {
  ButtonComponent,
  DetailsComponent,
  InputComponent,
  SelectComponent,
} from "@shopeasy-dashboard-ui/ui-library";
import { Skeleton } from "@mui/material";
import { clientData as globalClientData, GetClient } from "../../controllers/GetClient";
import {
  convertClientCurrencyToINR,
  ConvertCurrency,
  GetCurrencySymbol,
} from "../../utils/priceFormatter";

const VendorSettings = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    severity: "info",
  });

  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    commission: 10,
    minimum_withdraw_limit: 100,
    processing_days: 7,
    commission_type: "percentage",
    status: true,
    withdraw_Methods: ["upi"],
  });

  const currencySymbol = GetCurrencySymbol(globalClientData);

  const validateForm = (data = formData) => {
    const errs = {};

    const commissionNum = Number(data.commission);
    if (
      data.commission === "" ||
      data.commission === null ||
      data.commission === undefined ||
      isNaN(commissionNum)
    ) {
      errs.commission = "Commission rate is required";
    } else if (commissionNum < 0) {
      errs.commission = "Commission rate cannot be negative";
    } else if (data.commission_type === "percentage" && commissionNum > 100) {
      errs.commission = "Commission percentage cannot exceed 100%";
    }

    const minLimitNum = Number(data.minimum_withdraw_limit);
    if (
      data.minimum_withdraw_limit === "" ||
      data.minimum_withdraw_limit === null ||
      data.minimum_withdraw_limit === undefined ||
      isNaN(minLimitNum)
    ) {
      errs.minimum_withdraw_limit = "Minimum withdrawal threshold is required";
    } else if (minLimitNum <= 0) {
      errs.minimum_withdraw_limit = "Minimum withdrawal threshold must be greater than 0";
    }

    const procDaysNum = Number(data.processing_days);
    if (
      data.processing_days === "" ||
      data.processing_days === null ||
      data.processing_days === undefined ||
      isNaN(procDaysNum)
    ) {
      errs.processing_days = "Payout processing days is required";
    } else if (procDaysNum < 0) {
      errs.processing_days = "Payout processing days cannot be negative";
    }

    if (
      !data.withdraw_Methods ||
      !Array.isArray(data.withdraw_Methods) ||
      data.withdraw_Methods.length === 0
    ) {
      errs.withdraw_Methods = "At least one payout method must be selected";
    }

    return errs;
  };

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      await GetClient();
      const res = await API({
        url: "/admin/vendor/config",
        method: "get",
      });
      const config = res?.data?.data || res?.data?.config || res?.data || {};

      const rawMinLimit = config.minimum_withdraw_limit ?? 100;
      const convertedMinLimit = ConvertCurrency(rawMinLimit, globalClientData);

      const rawCommission = config.commission ?? 10;
      const commissionType = config.commission_type || "percentage";
      const convertedCommission =
        commissionType === "fixed"
          ? ConvertCurrency(rawCommission, globalClientData)
          : rawCommission;

      const rawProcessingDays = config.processing_days ?? 7;

      let rawMethods = config.withdraw_Methods || config.withdraw_methods || ["upi"];
      let parsedMethods = [];
      if (Array.isArray(rawMethods)) {
        parsedMethods = rawMethods;
      } else if (typeof rawMethods === "string" && rawMethods.trim()) {
        parsedMethods = rawMethods.includes(",")
          ? rawMethods.split(",").map((s) => s.trim())
          : [rawMethods.trim()];
      } else {
        parsedMethods = ["upi"];
      }

      setFormData({
        commission: convertedCommission,
        minimum_withdraw_limit: convertedMinLimit,
        processing_days: Number(rawProcessingDays),
        commission_type: commissionType,
        status: config.status !== undefined ? Boolean(config.status) : true,
        withdraw_Methods: parsedMethods,
      });
      setErrors({});
    } catch (err) {
      console.error("Error fetching vendor config:", err);
      setNotification({
        show: true,
        message:
          err?.response?.data?.message || err.message || "Failed to load marketplace configuration",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      const currentValidation = validateForm(next);
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        if (name === "commission_type" || name === "commission") {
          updatedErrors.commission = currentValidation.commission || "";
        } else {
          updatedErrors[name] = currentValidation[name] || "";
        }
        return updatedErrors;
      });
      return next;
    });
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setNotification({
        show: true,
        message: Object.values(validationErrors)[0] || "Please fix validation errors before saving.",
        severity: "error",
      });
      return;
    }

    setErrors({});
    setSubmitting(true);

    try {
      const convertedMinimumWithdrawLimit = convertClientCurrencyToINR(
        formData.minimum_withdraw_limit,
        globalClientData
      );

      const convertedCommission =
        formData.commission_type === "fixed"
          ? convertClientCurrencyToINR(formData.commission, globalClientData)
          : Number(formData.commission);

      const payload = {
        commission: Number(convertedCommission),
        minimum_withdraw_limit: Number(convertedMinimumWithdrawLimit),
        processing_days: Number(formData.processing_days),
        commission_type: formData.commission_type,
        status: Boolean(formData.status),
        withdraw_Methods: formData.withdraw_Methods,
      };

      await API({
        url: "/admin/vendor/config",
        method: "post",
        data: { vendorConfigObj: payload },
      });

      setNotification({
        show: true,
        message: "Marketplace configuration saved successfully!",
        severity: "success",
      });

      await fetchConfig();
    } catch (err) {
      console.error("Save vendor config error:", err);
      setNotification({
        show: true,
        message: err?.response?.data?.message || err.message || "Failed to save settings.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="h-auto overflow-y-auto pb-[130px] pt-5 px-5">
        <div className="w-full lg:w-[70%] xl:w-[60%] mx-auto space-y-4">
          <div className="flex items-center space-x-2 page-header-pb">
            <Skeleton variant="circular" width={28} height={28} />
            <div>
              <Skeleton variant="text" width={160} height={16} />
              <Skeleton variant="text" width={200} height={28} />
            </div>
          </div>
          <div className="secondary-bg-color rounded-[8px] border main-border-color shadow-sm p-6 space-y-4">
            <Skeleton variant="rectangular" width="100%" height={48} className="rounded-lg" />
            <Skeleton variant="rectangular" width="100%" height={48} className="rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {notification.show && (
        <Notification
          message={notification.message}
          close={() => setNotification((prev) => ({ ...prev, show: false }))}
          severity={notification.severity}
        />
      )}

      <div className="h-auto overflow-y-auto pb-[130px] pt-5 px-5">
        {/* Navigation & Header */}
        <div className="w-full lg:w-[70%] xl:w-[60%] mx-auto">
          <div className="flex items-center space-x-2 page-header-pb">
            <button
              onClick={(e) => {
                e.preventDefault();
                parentNavigate("/dashboard");
              }}
              className="back-arrow"
            >
              <ArrowLeft />
            </button>
            <div>
              <Breadcrumbs
                crumbs={[
                  { name: "Dashboard", path: "/dashboard" },
                  { name: "Vendor Settings", path: "/vendors/settings" },
                ]}
              />
              <h4 className="text-xl md:text-2xl font-semibold text-primary">
                Vendor Settings
              </h4>
            </div>
          </div>
        </div>

        {/* Main Settings Form */}
        <form
          onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
          method="POST"
          onSubmit={handleSubmit}
          className="w-full lg:w-[70%] xl:w-[60%] mx-auto flex flex-col gap-3"
        >
          <DetailsComponent
            open={true}
            hasHr={false}
            showChevron={false}
            className="!pt-5 sm:!pt-[25px]"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <SelectComponent
                label="Commission Type"
                required={true}
                name="commission_type"
                value={formData.commission_type}
                onChange={handleChange}
                options={[
                  { value: "percentage", label: "Percentage (%)" },
                  { value: "fixed", label: "Fixed Rate ($)" },
                ]}
              />

              <InputComponent
                label={`Commission Rate (${formData.commission_type === "percentage" ? "%" : currencySymbol})`}
                name="commission"
                type="number"
                step="0.01"
                required={true}
                value={formData.commission}
                onChange={handleChange}
                placeholder="10"
                error={errors.commission}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <InputComponent
                label={`Minimum Withdrawal Threshold (${currencySymbol})`}
                name="minimum_withdraw_limit"
                type="number"
                step="1"
                required={true}
                value={formData.minimum_withdraw_limit}
                onChange={handleChange}
                placeholder="100"
                error={errors.minimum_withdraw_limit}
              />

              <InputComponent
                label="Payout Processing Days"
                name="processing_days"
                type="number"
                step="1"
                min="0"
                required={true}
                value={formData.processing_days}
                onChange={handleChange}
                placeholder="7"
                error={errors.processing_days}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
              <SelectComponent
                label="Default Payout Method"
                required={true}
                name="withdraw_Methods"
                multiple={true}
                placeholder="Select payout methods"
                value={formData.withdraw_Methods}
                onChange={handleChange}
                error={errors.withdraw_Methods}
                options={[
                  { value: "paypal", label: "PayPal" },
                  { value: "bank_transfer", label: "Bank Transfer" },
                  { value: "upi", label: "UPI" },
                  { value: "stripe", label: "Stripe" },
                  { value: "razorpay", label: "Razorpay" },
                  { value: "payoneer", label: "Payoneer" },
                ]}
              />
            </div>
          </DetailsComponent>
        </form>

        {/* Bottom Fixed Action Save Bar */}
        <div className="flex items-center shadow fixed justify-between w-full bottom-[0px] z-50 secondary-bg-color border-t main-border-color p-3 left-0">
          <div className="w-[90%] flex items-center justify-between lg:w-[70%] xl:w-[60%] mx-auto">
            <ButtonComponent
              variant="link"
              icon={<ArrowLeft className="w-3" />}
              onClick={(e) => {
                e.preventDefault();
                parentNavigate("/vendors");
              }}
              className="text-primary"
            >
              Go To Vendors
            </ButtonComponent>

            <ButtonComponent
              variant="primary"
              size="md"
              loading={submitting}
              onClick={handleSubmit}
            >
              Save Settings
            </ButtonComponent>
          </div>
        </div>
      </div>
    </>
  );
};

export default VendorSettings;
